/* A salon's roles: what each kind of person there may use.
 *
 * The rows are `tenant_roles` (admin/server/schema/15-roles.sql). The rules the
 * database enforces are restated here only so the screen can say "no" before
 * the server does — the foreign key is what actually stops a held role being
 * deleted, and the guard trigger is what stops a built-in one going. If these
 * ever disagree with the SQL, the SQL wins and the screen is the bug.
 *
 * Pure functions over plain rows, like the rest of rules/.
 */
import { licensing } from './licensing.js';

/* The two every salon has.
 *
 * `owner` is what 09-owner-members.sql tests for to decide who manages the
 * salon's accounts; `station` is what every route writes when it does not name
 * a role. Both can be renamed and given different features. Neither can go. */
var BUILT_IN = ['owner', 'station'];

/* Features only the `owner` role may hold. The database enforces it (the
 * tenant_roles_owner_only trigger in 16-owner-only-features.sql, whose
 * private.owner_only_feature_ids() is the other copy of this list; a test
 * keeps the two equal). Restated here so the screen can say no first. */
var OWNER_ONLY = ['dreamcloud'];

function isBuiltIn(id) { return BUILT_IN.indexOf(id) !== -1; }

// The id is fixed at creation — tenant_members points at it — so it is held to
// the shape the CHECK constraint allows rather than to whatever was typed.
var ID_SHAPE = /^[a-z0-9][a-z0-9-]{0,39}$/;

function slugFor(name) {
  return String(name || '').trim().toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-').replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '').slice(0, 40).replace(/-+$/, '');
}

function sameName(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

/* What is wrong with a role about to be created, or null.
 *
 * `roles` is the salon's current list. Names are unique per salon ignoring
 * case and surrounding space — the same rule as the unique index — because two
 * roles both called "Front desk" is a People page nobody can read. */
function refuseCreate(draft, roles) {
  var name = String((draft && draft.name) || '').trim();
  var id = (draft && draft.id) || slugFor(name);
  if (!name) return 'A role needs a name.';
  if (!ID_SHAPE.test(id)) {
    return 'The id must be lower-case letters, digits and dashes, starting with a letter or digit.';
  }
  var list = roles || [];
  if (list.some(function (r) { return r.id === id; })) {
    return 'This salon already has a role with the id “' + id + '”.';
  }
  if (list.some(function (r) { return sameName(r.name, name); })) {
    return 'This salon already has a role called “' + name + '”.';
  }
  return null;
}

// The same question for a rename: the role may keep its own name.
function refuseRename(role, name, roles) {
  var next = String(name || '').trim();
  if (!next) return 'A role needs a name.';
  var clash = (roles || []).some(function (r) {
    return r.id !== role.id && sameName(r.name, next);
  });
  return clash ? 'This salon already has a role called “' + next + '”.' : null;
}

// How many members hold each role, by id.
function holders(members) {
  var out = {};
  (members || []).forEach(function (m) {
    out[m.role] = (out[m.role] || 0) + 1;
  });
  return out;
}

/* Why this role cannot be deleted, or null if it can.
 *
 * Built-in first: a built-in role with nobody in it still cannot go, and
 * saying "it is empty" and then refusing would be the screen contradicting
 * itself. */
function refuseDelete(role, members) {
  if (!role) return 'No such role.';
  if (isBuiltIn(role.id)) {
    return role.id === 'owner'
      ? 'Built in: the owner role is what manages this salon’s accounts.'
      : 'Built in: it is what new accounts are given when no role is named.';
  }
  var n = holders(members)[role.id] || 0;
  if (n) {
    return (n === 1 ? 'One person holds' : n + ' people hold') +
      ' this role. Move them to another role first.';
  }
  return null;
}

/* Why this feature cannot be added to this role, or null.
 *
 * Only ADDING is refused. Removing an owner-only feature from a non-owner role
 * is always allowed, so a stray one can be cleaned up. */
function refuseToggle(role, featureId) {
  if (OWNER_ONLY.indexOf(featureId) === -1) return null;
  if (!role || role.id === 'owner') return null;
  if ((role.features || []).indexOf(featureId) !== -1) return null;
  var meta = licensing.featureMeta(featureId);
  return 'Only the Owner role can have ' + (meta && meta.label ? meta.label : featureId) + '.';
}

/* A role's features with one switched. Normalised the way the plan editor
 * normalises, so a role's list reads the same here as it does in SQL. */
function toggle(features, id) {
  var list = features || [];
  var next = list.indexOf(id) !== -1
    ? list.filter(function (f) { return f !== id; })
    : list.concat([id]);
  return licensing.normalise(next);
}

/* What somebody in this role actually gets, given what the salon is licensed
 * for. The console's answer is this intersection — `tenant_entitlements()` in
 * 15-roles.sql takes it — so a role ticked for Reports at a salon whose plan has
 * no Reports gives nobody Reports, and the screen should say so.
 *
 * `licensed` null means the licence is not known yet (still loading): every
 * feature the role holds is shown as effective rather than as withheld. */
function effective(roleFeatures, licensed) {
  var mine = licensing.normalise(roleFeatures);
  if (!licensed) return mine;
  return mine.filter(function (f) { return licensed.indexOf(f) !== -1; });
}

// Built-ins first, in their seeded order, then the salon's own by sort_order
// and name — the order the People page's role menu reads in.
function sorted(roles) {
  return (roles || []).slice().sort(function (a, b) {
    var ab = isBuiltIn(a.id) ? BUILT_IN.indexOf(a.id) : BUILT_IN.length;
    var bb = isBuiltIn(b.id) ? BUILT_IN.indexOf(b.id) : BUILT_IN.length;
    if (ab !== bb) return ab - bb;
    if ((a.sort_order || 0) !== (b.sort_order || 0)) return (a.sort_order || 0) - (b.sort_order || 0);
    return String(a.name).localeCompare(String(b.name));
  });
}

export const roles = {
  BUILT_IN: BUILT_IN,
  OWNER_ONLY: OWNER_ONLY,
  ID_SHAPE: ID_SHAPE,
  isBuiltIn: isBuiltIn,
  slugFor: slugFor,
  refuseCreate: refuseCreate,
  refuseRename: refuseRename,
  holders: holders,
  refuseDelete: refuseDelete,
  refuseToggle: refuseToggle,
  toggle: toggle,
  effective: effective,
  sorted: sorted
};
